/** @sfdc-extension-file SNK */
'use client';

import { Suspense, useMemo, type ComponentProps } from 'react';
import type { ComponentDesignMetadata } from '@salesforce/storefront-next-runtime/design/react';
import { Region, type ComponentType } from '@/components/region';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { findMenuItemListItemsRegion } from '@/extensions/snk/lib/menu-item-list-overrides';
import { withComponentRegionDesignMetadata } from '@/extensions/snk/lib/merge-component-region-metadata';
import { cn } from '@/lib/utils';

interface MenuItemListProps extends ComponentProps<'div'> {
    categoryId?: string;

    regionId?: string;
    component?: ComponentType;
    componentData?: Record<string, Promise<unknown>>;
    designMetadata?: ComponentDesignMetadata;
    data?: unknown;
}

/* v8 ignore start - do not test decorators in unit tests, decorator functionality is tested separately*/
@Component('menuItemList', {
    name: 'Menu Item List',
    description:
        'Stack of Menu Item components for a single category. Used by the burger menu when the category has c_isMenuOverridden enabled in SCAPI.',
})
@RegionDefinition([
    {
        id: 'items',
        name: 'Menu items',
        description: 'Add Menu Item components in the order they should appear in the burger menu.',
        componentTypeInclusions: ['menuItem'],
    },
])
export class MenuItemListMetadata {
    @AttributeDefinition({
        id: 'categoryId',
        name: 'Category',
        description: 'SCAPI category ID that this list replaces when c_isMenuOverridden is enabled.',
        type: 'category',
        required: true,
    })
    categoryId?: string;
}
/* v8 ignore stop */

export default function MenuItemList({
    className,
    categoryId: _categoryId,
    regionId: _regionId,
    component,
    componentData: _componentData,
    designMetadata: _designMetadata,
    data: _data,
    ...props
}: MenuItemListProps) {
    const componentWithRegionMetadata = useMemo(
        () => (component ? withComponentRegionDesignMetadata(component, MenuItemListMetadata) : undefined),
        [component]
    );

    if (!componentWithRegionMetadata) {
        return null;
    }

    const itemsRegion = findMenuItemListItemsRegion(componentWithRegionMetadata);
    if (!itemsRegion) {
        return <div className={cn('flex w-full flex-col', className)} data-category-id={_categoryId} {...props} />;
    }

    return (
        <div className={cn('flex w-full flex-col', className)} data-category-id={_categoryId} {...props}>
            <Suspense fallback={null}>
                <Region component={componentWithRegionMetadata} regionId={itemsRegion.id} errorElement={null} />
            </Suspense>
        </div>
    );
}
